import { useEffect, useState } from 'react'
import useWorkflowNavigation from '@/frontend/pages/Dashboard/Workflows/useWorkflowNavigation'
import type { Filters, RecentExecutionEnvelope } from '@/frontend/pages/Dashboard/Workflows/types'

interface Context {
  organizationId: string | number; tenant: string | null; filters: Filters; workflows: string[]; revision: number; errorMessage: string
}

export default function useCatalogExecutions({ organizationId, tenant, filters, workflows, revision, errorMessage }: Context) {
  const { projectId, recentExecutionsPath } = useWorkflowNavigation()
  // Status/pagination filters belong to the general table, not the per-type history.
  const context = JSON.stringify([organizationId, projectId, tenant, filters.started_from, filters.started_to, workflows, revision])
  const [state, setState] = useState<{ context: string; rows: Record<string, RecentExecutionEnvelope> }>({ context, rows: {} })

  useEffect(() => {
    const controller = new AbortController()
    setState({ context, rows: {} })
    async function read(workflow: string) {
      const scope = { organization_id: organizationId, tenant, workflow_type: workflow, started_from: filters.started_from, started_to: filters.started_to }
      let envelope: RecentExecutionEnvelope
      try {
        const query = new URLSearchParams({ started_from: filters.started_from, started_to: filters.started_to })
        const response = await fetch(recentExecutionsPath(workflow, query), {
          credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal, cache: 'no-store',
        })
        const payload = await response.json() as RecentExecutionEnvelope
        if (String(payload.organization_id) !== String(organizationId) || payload.tenant !== tenant ||
          payload.workflow_type !== workflow || payload.started_from !== filters.started_from || payload.started_to !== filters.started_to ||
          (!response.ok && !payload.error) || (payload.data && (!Array.isArray(payload.data.items) || typeof payload.data.coverage?.complete !== 'boolean'))) {
          throw new Error('invalid_response')
        }
        envelope = payload
      } catch {
        if (controller.signal.aborted) return
        envelope = { ...scope, data: null, error: { code: 'unavailable', message: errorMessage } }
      }
      if (!controller.signal.aborted) setState((previous) => previous.context === context
        ? { context, rows: { ...previous.rows, [workflow]: envelope } } : previous)
    }
    async function load() {
      // At most two row requests are in flight; each server read has its own bounded budget.
      for (let index = 0; index < workflows.length && !controller.signal.aborted; index += 2) {
        await Promise.all(workflows.slice(index, index + 2).map(read))
      }
    }
    void load()
    return () => controller.abort()
    // The serialized key includes every boundary and avoids refetching on deferred prop object identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context])

  return state.context === context ? state.rows : {}
}
